import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface ShapefileLayerMeta {
  id: string;
  name: string;
  filename: string;
  originalname: string;
  size: number;
  uploadedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class FloorMapService {
  userToken: string;

  constructor(private http: HttpClient, @Inject('apiEndPoint') private apiEndPoint: string, @Inject('fileServerUrl') private fileServerUrl: string,) {
  }
  /*-- Import user API --*/
  importFloors(excelfile: any): any {
    return new Promise((resolve, reject) => {
      const formatData = new FormData();
      formatData.append('excelFile', excelfile);
      this.http.post(this.apiEndPoint + '/api/v1/floorMaps/import', formatData).subscribe(data => {
        resolve(data);
      }, error => {
        reject(error);
      });
    });
  }
  postFloorMapData(data: any) {
    return this.http.post(this.apiEndPoint + '/_/filterlist', data)
      .pipe(
        catchError((err) => {
          return throwError(JSON.stringify(err));    //Rethrow it back to component
        })
      )
  }

  generateRandomNum(
    length: number = 32,
    chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
  ) {
    var result = '';
    for (var i = length; i > 0; --i)
      result += chars[Math.floor(Math.random() * chars.length)];
    return result + new Date().valueOf();
  }




  getFloorMapDropdownData() {
    return this.http.get(this.apiEndPoint + '/gwdevice/maplist/_search?pretty&size=5000')
  }
  getGeoCoordinates(address) {
    return this.http.get('https://maps.googleapis.com/maps/api/geocode/json?address=' + address + '&key=AIzaSyC-_X985YnPXOeDchKKO5A0VBoliZERR2w&language=en')
  }
  getRegions() {
    return this.http.get('assets/jsons/countries.json')
  }
  deleteRecord(recordID: any) {
    return this.http.delete(this.apiEndPoint + '/gwdevice/maplist/' + recordID)
  }
  addMapData(formData: any) {

    return this.http.post(this.apiEndPoint + '/gwdevice/maplist/_create', formData);

  }
  updateMapData(formData: any, id: any) {
    return this.http.put(this.apiEndPoint + `/gwdevice/maplist/${id}/_update`, formData);
  }

  addMapDataFileserver(formData: any, boundaries=[], internalBoundaries=[]) {
    const filter = {payload:formData, boundaries, internalBoundaries}
    return this.http.post(this.fileServerUrl + '/create-maplist/imagedata', filter );
  }

  getConfigParam(name?:any){
    const filter = {"query":{"bool":{"must":[
      {"query_string":{ "query":name, "type": "phrase", "fields": ["_id"] } }
  ]
     }},"sort":{"_kuzzle_info.createdAt":"desc"}}
    return this.http.post(`${this.apiEndPoint}/gwdevice/configparam/_search?size=1`, filter)
  }

  createConfigParam(id: string, data: any) {
    return this.http.post(`${this.apiEndPoint}/gwdevice/configparam/_create?_id=${id}`, data)
  }

  updateConfigParam(id: string, data: any) {
    return this.http.patch(`${this.apiEndPoint}/gwdevice/configparam/${id}/_update`, data)
  }

  checkMap(id: any) {
    let formData = {
      "maps": [id]
    }
    return this.http.post(this.apiEndPoint + `/_/2.0/checkmap`, formData);
  }
  
  uploadFile(file: any, type: any) {
    const data = new FormData();
    // data.append('pdf', file)
    data.append('pdf', file, file.updatedName);
    let api = (type == 'PDF'||type == 'PNG') ? '/single' : '/upload'
    return this.http.post(this.fileServerUrl + api, data);
  }
  getMapFile(file:string){
    return this.http.get(`${this.fileServerUrl}/${file}`,{
      responseType: 'blob'
    });
  
  }

  createMaplistData(formData:any){
    return this.http.post(this.fileServerUrl + '/create-maplist/imagedata', formData);
  }

  getAllLocations(mapid:any){
    const filter = {"query":{"bool":{"must":[
        {"query_string":{ "query":`\"${mapid}\"`, "type": "phrase", "fields": ["mapid"] } }
   ]
       }},"sort":{"_kuzzle_info.createdAt":"desc"}}
    return this.http.post(`${this.apiEndPoint}/gwdevice/devicestaticlocation/_search?size=100`, filter)
}

clearLocations(devices=[]){

  const filter = {body:{"ids": devices}};
  return this.http.delete(`${this.apiEndPoint}/gwdevice/devicestaticlocation/_mDelete`, filter)
}

// ── Shapefile (customer infrastructure layer) APIs ───────────────────────────

uploadShapefileToServer(file: File, name: string): Observable<{ success: boolean; layer: ShapefileLayerMeta }> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  fd.append('name', name);
  return this.http.post<{ success: boolean; layer: ShapefileLayerMeta }>(`${this.fileServerUrl}/shapefiles/upload`, fd);
}

getShapefileLayers(): Observable<{ success: boolean; layers: ShapefileLayerMeta[] }> {
  return this.http.get<{ success: boolean; layers: ShapefileLayerMeta[] }>(`${this.fileServerUrl}/shapefiles/list`);
}

getShapefileData(filename: string): Observable<ArrayBuffer> {
  return this.http.get(`${this.fileServerUrl}/shapefiles/file/${filename}`, { responseType: 'arraybuffer' });
}

deleteShapefileLayer(id: string): Observable<{ success: boolean }> {
  return this.http.delete<{ success: boolean }>(`${this.fileServerUrl}/shapefiles/${id}`);
}

// ── Shapefile parsing & client-side simplification ───────────────────────────
// Shared by floor-maps-manager and outdoor-map — keep logic in one place.

private readonly RDP_TOLERANCE = 0.0001; // ~11 m at equator

async parseShapefile(buffer: ArrayBuffer, filename = ''): Promise<any> {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'geojson') {
    const text    = new TextDecoder().decode(buffer);
    const geojson = JSON.parse(text);
    return this.simplifyGeoJson(geojson);
  }
  // .shp — passing { shp: buffer } triggers shpjs's fromObject() path which
  // parses raw .shp geometry directly. Passing a bare ArrayBuffer always hits
  // parseZip() and throws because a .shp file is not a zip archive.
  const shpjs  = await import('shpjs');
  const shp    = (shpjs as any).default ?? shpjs;
  const raw    = await shp({ shp: buffer });
  const cols: any[] = Array.isArray(raw) ? raw : [raw];
  const geojson = cols.length === 1
    ? cols[0]
    : { type: 'FeatureCollection', features: cols.flatMap((c: any) => c.features ?? []) };
  return this.simplifyGeoJson(geojson);
}

simplifyGeoJson(geojson: any): any {
  const simplifyRing = (ring: number[][]): number[][] => {
    if (ring.length < 5) return ring;
    const simplified = this.rdpSimplify(ring.slice(0, -1));
    return simplified.length >= 3 ? [...simplified, [...simplified[0]]] : ring;
  };
  const walk = (geom: any): void => {
    if (!geom) return;
    switch (geom.type) {
      case 'LineString':        geom.coordinates = this.rdpSimplify(geom.coordinates); break;
      case 'MultiLineString':   geom.coordinates = geom.coordinates.map((l: number[][]) => this.rdpSimplify(l)); break;
      case 'Polygon':           geom.coordinates = geom.coordinates.map(simplifyRing); break;
      case 'MultiPolygon':      geom.coordinates = geom.coordinates.map((p: number[][][]) => p.map(simplifyRing)); break;
      case 'GeometryCollection':(geom.geometries || []).forEach(walk); break;
      case 'Point':             geom.coordinates = this.roundPt(geom.coordinates); break;
      case 'MultiPoint':        geom.coordinates = geom.coordinates.map((p: number[]) => this.roundPt(p)); break;
    }
  };
  const features = geojson.type === 'FeatureCollection' ? geojson.features : [geojson];
  features.forEach((f: any) => walk(f?.geometry));
  return geojson;
}

private rdpSimplify(pts: number[][]): number[][] {
  if (pts.length <= 2) return pts.map(p => this.roundPt(p));
  const [x1, y1] = pts[0], [x2, y2] = pts[pts.length - 1];
  const dx = x2 - x1, dy = y2 - y1, lenSq = dx * dx + dy * dy;
  let maxDist = 0, maxIdx = 1;
  for (let i = 1; i < pts.length - 1; i++) {
    const [px, py] = pts[i];
    const dist = lenSq === 0
      ? Math.sqrt((px - x1) ** 2 + (py - y1) ** 2)
      : Math.abs(dy * px - dx * py + x2 * y1 - y2 * x1) / Math.sqrt(lenSq);
    if (dist > maxDist) { maxDist = dist; maxIdx = i; }
  }
  if (maxDist > this.RDP_TOLERANCE) {
    const left  = this.rdpSimplify(pts.slice(0, maxIdx + 1));
    const right = this.rdpSimplify(pts.slice(maxIdx));
    return [...left.slice(0, -1), ...right];
  }
  return [this.roundPt(pts[0]), this.roundPt(pts[pts.length - 1])];
}

private roundPt(p: number[]): number[] {
  return [+(p[0].toFixed(5)), +(p[1].toFixed(5)), ...p.slice(2)];
}
}
